import { useConfiguratorStateDomain } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import {
  findActiveModelOption,
  findProduct,
} from "@/configurator/domain/logic/catalog";
import {
  buildRendererCommands,
  getPrimarySwatchColor,
} from "@/configurator/domain/logic/rendererCommands";
import type {
  ModelOption,
  RendererCommand,
  SceneConfig,
} from "@/configurator/domain/models/types";
import type { ConfiguratorSnapshot } from "@/configurator/domain/ports/configuratorState";

/*
 * Module scope is load-bearing here: `SceneRenderer` runs an effect keyed on
 * `commands`, so a fresh array per render would re-apply the whole
 * configuration to the scene on every render instead of when it changes.
 */
const selectRendererCommands = (s: ConfiguratorSnapshot) =>
  buildRendererCommands(
    findProduct(s.config.catalog, s.activeProductId),
    s.selectedOptions,
  );

const selectActiveModel = (s: ConfiguratorSnapshot) =>
  findActiveModelOption(s.config, s.selectedModelId);

const selectScene = (s: ConfiguratorSnapshot) => s.config.scene;

const selectPrimarySwatchColor = (s: ConfiguratorSnapshot) =>
  getPrimarySwatchColor(
    findProduct(s.config.catalog, s.activeProductId),
    s.selectedOptions,
  );

export type ViewerDomain = {
  activeModel: ModelOption | undefined;
  scene: SceneConfig;
  /** What the current selection asks the 3D scene to do. */
  commands: RendererCommand[];
};

/**
 * What the 3D viewer needs: the model to load, how to set the scene up, and the
 * commands the current configuration resolves to. The mapping from selected
 * options to renderer parameters belongs here and not in the renderer — see
 * Renderer Rules in PROJECT-RULES.md.
 */
export function useViewerDomain(): ViewerDomain {
  const { useSelect } = useConfiguratorStateDomain();

  return {
    activeModel: useSelect(selectActiveModel),
    scene: useSelect(selectScene),
    commands: useSelect(selectRendererCommands),
  };
}

/** The 2D placeholder paints one fill, taken from the first swatch group. */
export function usePrimarySwatchDomain(): string {
  const { useSelect } = useConfiguratorStateDomain();
  return useSelect(selectPrimarySwatchColor);
}
